import { StoreId } from "../decorators/StoreIdDecorator";
import { getObjectStoreIdString } from "../decorators/StoreIdHelper";
import { TemplateType } from "../types/TemplateType";
import { ModelStatus } from "../types/ModelStatus";
import API from "./API";
import { Comment } from "./Comment";
import { ColumnMatcher } from "./ColumnMatcher";
import { DocFieldConfig } from "./config/DocFieldConfig";
import DocTypeConfig from "./config/DocTypeConfig";
import { DlfMatcher, DlfMatcherProperties } from "./matchers/DlfMatcher";
import { MatcherFactory } from "./matchers/factory/MatcherFactory";
import { Matcher, MatcherProperties } from "./matchers/Matcher";
import { RegexMatcher } from "./matchers/RegexMatcher";
import ModelImages from "./ModelImages";
import { TableConfig } from "./config/TableConfig";
import { TableModel, TableModelData } from "./TableModel";
import { TransformRuleFactory } from "./transformation/factory/TransformRuleFactory";
import {
  TransformRule,
  TransformRuleData,
} from "./transformation/TransformRule";
import {
  setupRulesets,
  ValidationRuleSet,
} from "./validation/ValidationRuleSet";
import { ValidationRule } from "./validation/ValidationRule";
import { AcceleratorDocStoreKey } from "./AcceleratorDoc";

interface ModelStoreKey {
  name: string;
  batchClassIdentifier: string;
  docType?: string;
}
type ModelData = {
  uuid?: string;
  version?: number;

  name: string;
  batchClassIdentifier: string;
  docType: string;

  comments: Comment[];

  /**
   * Rely on this property for determining if Unknown or not
   */
  // unknown: boolean;
  templateType: TemplateType;
  status?: ModelStatus;
  enabled: boolean;
  forceValidate: boolean;

  documentStartRules: MatcherProperties[];
  documentEndRules: MatcherProperties[];
  dlfRules: DlfMatcherProperties[];
  tables: TableModelData[];

  modelPagesSize?: number;
  /* Set by FE. */
  changed?: boolean;

  validators: ValidationRuleSet[];

  transformationRules: TransformRuleData[];

  inheritTransformRules: boolean;
  /* Unix Epoch in MS of when created. */
  created?: number;
  /* Unix Epoch in MS of when last modified. */
  lastModified?: number;
  /* String value representing who last modified model. */
  modifiedBy?: string;
  /**
   * Defined if current model is considered "stale". This indicates that there is a newer version of the Model the
   * user might prefer.
   * If staleness is not a concern, this.version can be set to this.knownHigherVersion.
   * Set by FE.
   */
  knownHigherVersion?: number;
};
export { ModelData, ModelStoreKey };

class Model {
  changed: boolean;
  // included only to allow for renaming in Database
  uuid?: string;
  version: number;

  comments: Comment[];

  templateType: TemplateType;
  status: ModelStatus;
  // unknown: boolean;
  @StoreId
  storeId: ModelStoreKey;
  docType: string;
  enabled: boolean;
  forceValidate: boolean;

  documentStartRules: Matcher[];
  documentEndRules: Matcher[];
  dlfRules: DlfMatcher[];
  tables: TableModel[];

  validators: ValidationRuleSet[];

  transformationRules: TransformRule[];

  inheritTransformRules: boolean;

  modelPagesSize?: number;

  /* Unix Epoch in MS of when created. */
  created?: number;
  /* Unix Epoch in MS of when last modified. */
  lastModified?: number;
  /* String value representing who last modified model. */
  modifiedBy?: string;

  /**
   * Defined if current model is considered "stale". This indicates that there is a newer version of the Model the
   * user might prefer.
   * If staleness is not a concern, this.version can be set to this.knownHigherVersion
   */
  knownHigherVersion?: number;

  _firstModified?: AcceleratorDocStoreKey;

  get unknown(): boolean {
    return (
      this.templateType === "UNKNOWN" ||
      this.getName() === "" ||
      this.docType === "Unknown"
    );
  }

  get general(): boolean {
    return this.templateType === "GENERAL" || this.templateType === "FORMAT";
  }

  constructor(data: ModelData) {
    this.storeId = {
      name: data.name,
      batchClassIdentifier: data.batchClassIdentifier,
      docType: data.docType,
    };
    this.uuid = data.uuid;
    this.version = data.version ?? 0;
    this.docType = data.docType;
    this.templateType = data.templateType;
    this.status = data.status ?? "MANUAL";

    this.comments = data.comments;
    if (this.comments == null) {
      this.comments = [];
    }

    this.sortComments();

    this.enabled = this.unknown ? true : data.enabled;
    this.forceValidate = data.forceValidate;

    this.documentStartRules = data.documentStartRules
      .map(MatcherFactory.createMatcher)
      .sort((a, b) => a.position - b.position);
    this.documentEndRules = data.documentEndRules
      .map(MatcherFactory.createMatcher)
      .sort((a, b) => a.position - b.position);

    this.dlfRules = data.dlfRules.map((dlfRule) => {
      return MatcherFactory.createDlfMatcher(dlfRule);
    });

    this.tables = data.tables.map((r) => new TableModel(r));
    this.modelPagesSize = data.modelPagesSize;
    this.changed = data.changed ?? false;

    this.validators = setupRulesets(data);

    this.transformationRules = (data.transformationRules ?? [])
      .sort((a, b) => a.position - b.position)
      .map(TransformRuleFactory.createRule);

    this.inheritTransformRules = data.inheritTransformRules;

    this.created = data.created;
    this.lastModified = data.lastModified;
    this.modifiedBy = data.modifiedBy;

    this.knownHigherVersion = data.knownHigherVersion;
  }

  get stale(): boolean {
    return (
      this.knownHigherVersion != null && this.knownHigherVersion > this.version
    );
  }

  toModelData(): ModelData {
    return {
      uuid: this.uuid,
      batchClassIdentifier: this.storeId.batchClassIdentifier,
      name: this.storeId.name,
      dlfRules: this.dlfRules.map((matcher) => matcher.toMatcherData()),
      docType: this.docType,
      comments: this.comments,
      documentStartRules: this.documentStartRules.map((matcher) =>
        matcher.toMatcherData(),
      ),
      documentEndRules: this.documentEndRules.map((matcher) =>
        matcher.toMatcherData(),
      ),
      templateType: this.templateType,
      status: this.status,
      tables: this.tables.map((table) => table.toTableModelData()),
      modelPagesSize: this.modelPagesSize,
      changed: this.changed,
      enabled: this.enabled,
      forceValidate: this.forceValidate,
      validators: this.validators,
      transformationRules: this.transformationRules.map((x) => x.toData()),
      inheritTransformRules: this.inheritTransformRules,
      lastModified: this.lastModified,
      created: this.created,
      modifiedBy: this.modifiedBy,
      knownHigherVersion: this.knownHigherVersion,
      version: this.version,
    };
  }

  setTemplateType(templateType: TemplateType) {
    this.templateType = templateType;
    this.setChanged(true);
  }

  setStatus(status: ModelStatus) {
    this.status = status;
    this.setChanged(true);
  }

  setVersion(version: number) {
    console.debug(
      `Setting Model ${getObjectStoreIdString(this)} as version=${version}`,
    );
    this.version = version;
  }

  setUnknown(unknown: boolean) {
    //console.debug(`Setting Model ${getObjectStoreIdString(this)} as unknown=${unknown + ''}`);
    // this.unknown = unknown;
    if (unknown) {
      this.templateType = "UNKNOWN";
    }
  }
  setChanged(changed: boolean) {
    // console.trace(`Setting Model ${getObjectStoreIdString(this)} as changed=${changed + ''}`);
    this.changed = changed;
  }

  setIsGeneral(type: "GENERAL" | "FORMAT") {
    //console.debug(`Setting Model ${getObjectStoreIdString(this)} as IsGeneral=${isGeneral + ''}`);
    // this.isGeneral = isGeneral;
    this.templateType = type;
  }

  getBatchClassIdentifier(): string {
    return this.storeId.batchClassIdentifier;
  }
  getName(): string {
    return this.storeId.name;
  }

  setName(newName: string) {
    console.log(
      `Changing Model name of ${getObjectStoreIdString(this)} to ${newName}`,
    );
    if (newName === "Unknown") return;
    this.storeId.name = newName;
    // set unknown to false
    if (this.unknown) {
      this.setTemplateType("NORMAL");
    }
    this.setChanged(true);
  }

  changeDocType(docType: DocTypeConfig) {
    if (this.docType === docType.typeName) {
      this.mergeDocType(docType);
    } else {
      this.docType = docType.typeName;
      this.dlfRules = docType.fields.map((field, i) => {
        const matcher = MatcherFactory.defaultMatcherFrom(field);
        matcher.position = i;
        return matcher;
      });
      this.validators = docType.fields.map((field, i) => ({
        fieldName: field.fieldName,
        clientValidationOperator: "OR",
        validationRules: [],
      }));
      this.tables = docType.tables.map((x) => TableModel.createFromConfig(x));
      this.setChanged(true);
    }
  }

  /**
   * Creates blank DLF matchers and Table models for DocFieldConfig and TableConfig's that don't exist
   * on the Model. Only applies if a DocFieldConfig has been added after the Model was configured.
   * Creates ValidationRuleSets for all DLFs and Columns.
   * @param docType
   */

  private mergeDocType(docType: DocTypeConfig) {
    const existingDlfs = this.dlfRules.map((dlf) => dlf.fieldName);
    const existingTables = this.tables.map((table) => table.name);
    const existingValidationRuleSets = this.validators.map((x) => x.fieldName);

    const newDlfs = docType.fields.filter(
      (field) => !existingDlfs.includes(field.fieldName),
    );
    const newTables = docType.tables.filter(
      (table) => !existingTables.includes(table.name),
    );
    const newValidationRuleSets = docType.fields.filter(
      (x) => !existingValidationRuleSets.includes(x.fieldName),
    );

    // add new DLF ValidationRuleSets
    this.validators.push(
      ...newValidationRuleSets.map(
        (x): ValidationRuleSet => ({
          fieldName: x.fieldName,
          clientValidationOperator: "OR",
          validationRules: [],
        }),
      ),
    );
    // add new tables
    this.tables.push(
      ...newTables.map((tableConfig) =>
        TableModel.createFromConfig(tableConfig),
      ),
    );
    this.dlfRules.push(
      ...newDlfs.map((docFieldConfig) => {
        const matcher = MatcherFactory.defaultMatcherFrom(docFieldConfig);
        return matcher;
      }),
    );
    // ensure position unique on all DLFs
    this.dlfRules.map((matcher, index) => (matcher.position = index));

    // Ensure existing Tables are valid
    this.tables.forEach((existingTable) => {
      const tableConfig = docType.tables.find(
        (t) => t.name === existingTable.name,
      );
      if (tableConfig) {
        this.mergeTableColumns(existingTable, tableConfig);

        // ensure ValidationRuleSet exists for each ColumnMatcher
        const columnsWithoutValidationRuleSet = existingTable.columns.filter(
          (c) =>
            !existingTable.validators.find((v) => v.fieldName === c.columnName),
        );
        existingTable.validators.push(
          ...columnsWithoutValidationRuleSet.map(
            (x): ValidationRuleSet => ({
              fieldName: x.columnName,
              clientValidationOperator: "OR",
              validationRules: [],
            }),
          ),
        );
      }
    });
  }

  private mergeTableColumns(tableModel: TableModel, tableConfig: TableConfig) {
    const existingColumns = tableModel.columns.map((x) => x.columnName);
    const newColumns = tableConfig.columnNames.filter(
      (column) => !existingColumns.includes(column.fieldName),
    );

    tableModel.columns.push(
      ...newColumns.map((column) => {
        const matcherData = ColumnMatcher.createFromConfig(column);
        return new ColumnMatcher(matcherData);
      }),
    );
  }

  addStartRule(regexMatcher: Matcher) {
    this.documentStartRules.push(regexMatcher);
    regexMatcher.position = this.documentStartRules.length;
    this.setChanged(true);
  }

  addEndRule(regexMatcher: Matcher) {
    this.documentEndRules.push(regexMatcher);
    regexMatcher.position = this.documentEndRules.length;
    this.setChanged(true);
  }

  removeStartRule(regexMatcher: Matcher) {
    // TODO: does this even work?
    const index = this.documentStartRules.indexOf(regexMatcher);
    if (index === -1) return;
    this.documentStartRules.splice(index, 1);
    this.updateStartRulePositions(index);
    this.setChanged(true);
  }

  removeEndRule(regexMatcher: Matcher) {
    // TODO: does this even work?
    const index = this.documentEndRules.indexOf(regexMatcher);
    if (index === -1) return;
    this.documentEndRules.splice(index, 1);
    this.updateEndRulePositions(index);
    this.setChanged(true);
  }

  /**
   * Insert a new Start Rule at the location of an old Matcher Id.
   * @param matcher
   * @param oldId
   */
  replaceStartRule(matcher: Matcher, index: number) {
    if (index === -1) return this.addStartRule(matcher);
    this.documentStartRules.splice(index, 1, matcher);
    this.updateStartRulePositions(index);
    this.setChanged(true);
  }

  /**
   * Insert a new End Rule at the location of an existing Matcher, otherwise add a new Matcher.
   * @param matcher
   * @param oldId
   */
  replaceEndRule(matcher: Matcher, index: number) {
    if (index === -1) return this.addEndRule(matcher);
    this.documentEndRules.splice(index, 1, matcher);
    this.updateEndRulePositions(index);
    this.setChanged(true);
  }

  private updateStartRulePositions(index = 0) {
    for (let i = index; i < this.documentStartRules.length; i++) {
      this.documentStartRules[i].position = i;
    }
  }

  private updateEndRulePositions(index = 0) {
    for (let i = index; i < this.documentEndRules.length; i++) {
      this.documentEndRules[i].position = i;
    }
  }

  updateDlf(dlfMatcher: DlfMatcher) {
    let index = this.dlfRules.findIndex(
      (val: DlfMatcher) => val.fieldName === dlfMatcher.fieldName,
    );
    let deleteCount = 1;
    // console.log(`${index} ${deleteCount} dlfMatcher.fieldName=${dlfMatcher.fieldName} `)
    // console.debug(`${this.dlfRules[index]?.getMatcherId()}\n${dlfMatcher.getMatcherId()}`)
    if (index === -1) {
      deleteCount = 0;
      index = 0;
    }
    this.dlfRules.splice(index, deleteCount, dlfMatcher);
    this.setChanged(true);
  }

  getDlfMatcher(field: DocFieldConfig | string): DlfMatcher | undefined {
    let fieldName: string;
    if (typeof field === "string") {
      fieldName = field;
    } else {
      fieldName = field.fieldName;
    }
    return this.dlfRules.find((val) => val.fieldName === fieldName);
  }

  getTableModel(name: string): TableModel | undefined {
    return this.tables.filter((x) => x.name === name)[0];
  }

  getTableModelIndex(name: string): number {
    return this.tables.map((x) => x.name).indexOf(name);
  }

  setTableModel(tableModel: TableModel) {
    // find table model
    let index = this.tables.findIndex(
      (val: TableModel) => val.name === tableModel.name,
    );
    let deleteCount = 1;
    if (index === -1) {
      deleteCount = 0;
      index = 0;
    }
    this.tables.splice(index, deleteCount, tableModel);
    this.setChanged(true);
  }

  getTransformRules(fieldName: string): TransformRule[] {
    return this.transformationRules.filter((x) => x.fieldName === fieldName);
  }
  /**
   *
   * @param fieldName
   * @param transformRules
   * @returns  All TransformRules
   */
  setTransformRules(
    fieldName: string,
    transformRules: TransformRule[],
  ): TransformRule[] {
    this.transformationRules = [
      ...transformRules,
      ...this.transformationRules.filter((x) => x.fieldName !== fieldName),
    ];
    this.setChanged(true);
    return this.transformationRules;
  }

  getValidationRuleSet(fieldName: string): ValidationRuleSet {
    return (
      this.validators.find((x) => x.fieldName === fieldName) ?? {
        clientValidationOperator: "OR",
        fieldName,
        validationRules: [],
      }
    );
  }
  /**
   *
   * @param fieldName
   * @param ruleset
   * @returns All ValidationRuleSets
   */
  setValidationRuleSet(
    fieldName: string,
    ruleset: ValidationRuleSet,
  ): ValidationRuleSet[] {
    this.validators = [
      ruleset,
      ...this.validators.filter((x) => x.fieldName !== fieldName),
    ];
    this.setChanged(true);
    return this.validators;
  }

  /**
   * Only add comments that have a non-whitespace comment.
   */
  addComment(comment: Comment) {
    if (comment.comment.trim() === "") return;
    this.comments.push(comment);
    this.sortComments();
  }

  setComments(comments: Comment[]) {
    this.comments = comments.filter((x) => x.comment.trim().length !== 0);
    this.sortComments();
  }
  /**
   * Sort comments reverse chronologically, then move unsaved comments to the top of the array by insert order.
   */
  private sortComments() {
    const saved = this.comments.filter((x) => x.created != undefined);

    saved.sort((a, b) => b.created! - a.created!);

    this.comments = this.comments
      .filter((x) => x.created == undefined)
      .concat(saved);
  }

  static async load(name: string, batchClassId: string): Promise<Model | null> {
    return API.get(`/v0/model/${batchClassId}/${encodeURIComponent(name)}`)
      .then((value: Response) => value.json())
      .then((value: any): Model => new Model(value))
      .catch(() => {
        return null;
      });
  }

  getModelImages(): ModelImages {
    return new ModelImages(this.modelPagesSize ?? 0, {
      modelName: this.storeId.name,
      batchClassIdentifier: this.storeId.batchClassIdentifier,
      uuid: this.uuid ?? "",
    });
  }

  public static createTest(rest: Partial<ModelData> = {}): Model {
    const defaultModel: ModelData = {
      uuid: "",
      batchClassIdentifier: "",
      name: "",
      docType: "",
      comments: [],
      templateType: "NORMAL",
      documentStartRules: [],
      documentEndRules: [],
      dlfRules: [],
      tables: [],
      modelPagesSize: 0,
      enabled: true,
      forceValidate: false,
      validators: [],
      transformationRules: [],
      inheritTransformRules: false,
      created: undefined,
      lastModified: undefined,
      modifiedBy: undefined,
      changed: undefined,
      knownHigherVersion: undefined,
      version: undefined,
    };

    return new Model({
      ...defaultModel,
      ...rest,
    });
  }
}

export default Model;
